<script lang="ts">
	import { _ } from 'svelte-i18n';
	import { getHubStores } from '$lib/stores/hubStores';
	import { atLeastModerator, getRole, RoleValues } from '$lib/utils/roles';
	import type { presenterShortCollection } from '$lib/api/rpc';
	import Icon from '$lib/components/Icon/Icon.svelte';
	import { toastMessages } from '$lib/stores/toastMessageStore';
	import { createEventDispatcher } from 'svelte';
	import { CheckBox } from '$lib/components/Inputs';
	import { popup } from '$lib/actions/Popup/popup';
	import type { PopupSettings } from '$lib/actions/Popup/types';
	import type { Collection } from '$lib/types/Collections';
	const { collections, hub, api } = getHubStores();

	const userRole = getRole($hub.user_context);
	let writableCollections = $collections.filter(
		(collection) => RoleValues[collection.min_role] <= RoleValues[userRole]
	);

	export let attachAllCollection = false;
	export let allCollection = {};
	export let showAddCollection = atLeastModerator($hub.user_context);
	export let showHideFromAll = true;

	if (attachAllCollection) {
		writableCollections.unshift(allCollection as Collection);
	}

	export let selectedCollection: Collection | presenterShortCollection =
		writableCollections.find((collection) => {
			return collection.id == $hub.settings.default_collection_id;
		}) || writableCollections[0];

	export let selectedCollectionId = selectedCollection.id;

	export let hideFromAll = false;

	$: selectedCollectionId = selectedCollection.id;

	let showNewCollection = false;

	const selectCollection = (collection: Collection) => {
		// Set hideFromAll to false if the All collection is selected
		if (collection.id == null) {
			hideFromAll = false;
		}
		selectedCollection = collection;
	};

	let newCollection = '';
	const cancelAddCollection = () => {
		newCollection = '';
		showNewCollection = false;
	};

	$: disableSave = !newCollection.trim().length;
	const addCollection = async () => {
		if (disableSave) return;
		try {
			const resp = await api.createCollection({ hubId: $hub.id, name: newCollection });
			selectedCollection = resp.collection;
			$collections = [...$collections, resp.collection];
			writableCollections = $collections;
		} catch (e) {
			//@ts-ignore
			toastMessages.danger(e.cause || e.error);
		}
		newCollection = '';
		showNewCollection = false;
	};

	const dispatchEvent = createEventDispatcher();

	let popupCombobox: PopupSettings = {
		event: 'click',
		target: 'combobox',
		placement: 'top',
		closeQuery: '',
	};
</script>

<div data-popup="combobox" class="collectionMenu">
	{#if $hub.settings.feature.nextGenUI && showHideFromAll}
		<div class="hideFromAll">
			<CheckBox
				bind:checked={hideFromAll}
				name="hideFromAll"
				placeholder="Hide from All collection"
				disabled={selectedCollection.id == null} />
		</div>
	{/if}
	<div class="collections">
		{#each writableCollections as collection}
			<button
				type="button"
				class="collection"
				class:isSelected={collection.id == selectedCollection.id}
				on:click={() => {
					selectCollection(collection);
					dispatchEvent('collectionSelected', { collection: selectedCollection });
				}}>
				{collection.name}
			</button>
		{/each}
	</div>
	{#if showAddCollection}
		<div class="addCollection">
			{#if showNewCollection}
				<div class="newCollection">
					<!-- svelte-ignore a11y-autofocus -->
					<input
						autofocus
						placeholder={$_('hub.settings.collections.tags.new.collection')}
						bind:value={newCollection} />
					<button type="button" id="cancel" on:click={cancelAddCollection}
						>{$_('common.cancel')}</button>
					<span> • </span>
					<button type="button" id="save" class:disabled={disableSave} on:click={addCollection}
						>{$_('common.save')}
					</button>
				</div>
			{:else}
				<button
					type="button"
					class="addCollectionButton collection"
					on:click={() => (showNewCollection = true)}>
					<Icon icon="add" size="25" color="grey" />
					{$_('post.add.collection')}
				</button>
			{/if}
		</div>
	{/if}
</div>
<button type="button" class="box" use:popup={popupCombobox}>
	<div class="boxText">{`${$_('share.collection.label')} ${selectedCollection.name}`}</div>
	<Icon icon="dropdown-arrow" size="25" color="black" />
</button>
<input type="hidden" bind:value={selectedCollection} name="selectedCollection" />

<style>
	.box {
		display: flex;
		align-items: center;
		block-size: 36px;
		inline-size: 200px;
		padding-inline: 5px;
		border: 1px solid var(--grayLight);
		border-radius: var(--gutterSmaller);
		background-color: white;
	}

	.boxText {
		flex-grow: 1;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.hideFromAll {
		padding: var(--gutterSmall);
		border-block-end: 1px solid var(--grayLight);
	}

	.collectionMenu {
		max-inline-size: 300px;
		overflow: hidden;
		border-radius: var(--gutterSmaller);
		background-color: white;
		box-shadow: rgb(0 0 0 / 20%) 0 0 18px 0;
	}

	.collections {
		max-block-size: 200px;
		overflow: scroll;
	}

	.collection {
		inline-size: 100%;
		padding: var(--gutterSmall);
		border: none;
		background-color: white;
		border-block-end: 1px solid var(--grayLight);
		text-align: start;
	}

	.collection:hover {
		background-color: var(--grayLighter);
		cursor: pointer;
	}

	.isSelected,
	.isSelected:hover {
		background-color: var(--grayLight);
	}

	.addCollection {
		inline-size: 100%;
		border-block-start: 1px solid var(--grayLight);
		text-align: start;
	}

	.addCollectionButton {
		inline-size: 100%;
		border: 0;
		color: grey;
		cursor: pointer;
	}

	.newCollection {
		padding: var(--gutterMedium);
		color: grey;
	}

	input {
		inline-size: 100%;
		margin-block-end: var(--gutterSmall);
		padding-block: var(--gutterSmall);
		border: 0;
		border-block-end: 1px solid var(--grayLight);
	}

	#cancel,
	#save {
		all: unset;
		cursor: pointer;
	}

	#cancel {
		color: var(--lightenedSecondaryAccentColor);
	}

	#save {
		color: var(--lightenedPrimaryAccentColor);
	}

	.disabled {
		color: rgb(129 186 246) !important;
		cursor: default !important;
	}
</style>
